import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { Link } from "react-router-dom";
import { type MenuLink, AppSection } from ".";
import { XMarkIcon } from "@heroicons/react/20/solid";

type Props = {
  links: MenuLink[];
  isOpen: boolean;
  onClose: () => void;
  onSectionChange?: (section: AppSection) => void;
};

function MobileHeaderMenu({ links, isOpen, onClose, onSectionChange }: Props) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 flex justify-between items-center"
                >
                  Menu
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </Dialog.Title>
                <div className="mt-2">
                  <ul className="flex flex-col gap-2">
                    {links
                      .filter((link) => link.mobileVisibility)
                      .sort((a, b) => a.order - b.order)
                      .map((link, index) => {
                        const Icon = link.icon;
                        // If it has action, render a button
                        if (link.action && onSectionChange) {
                          return (
                            <li key={index}>
                              <button
                                onClick={() => {
                                  onSectionChange(link.action as AppSection);
                                  onClose();
                                }}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-primary/10 transition-colors w-full text-left"
                              >
                                <Icon className="h-5 w-5" />
                                <span>{link.name}</span>
                              </button>
                            </li>
                          );
                        }
                        // If it has url, render a Link
                        else if (link.url) {
                          return (
                            <li key={index}>
                              <Link
                                to={link.url}
                                onClick={onClose}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-primary/10 transition-colors"
                              >
                                <Icon className="h-5 w-5" />
                                <span>{link.name}</span>
                              </Link>
                            </li>
                          );
                        }
                        // Otherwise, return null
                        return null;
                      })}
                  </ul>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default MobileHeaderMenu; 